import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, UserCog, LogOut, HelpCircle, Trophy, Users, Calendar } from 'lucide-react';
import QuizCard from './QuizCard';
import { getAllQuizzes, getQuizStats, deleteQuiz } from '../../services/quizService';
import { getFinishedSessions } from '../../services/sessionService';
import { useAuth } from '../../context/AuthContext';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 6) return 'God natt';
  if (hour < 12) return 'God morgon';
  if (hour < 18) return 'God ettermiddag';
  return 'God kveld';
}

const PREVIEW_COUNT = 3;

export default function HostDashboard({ onCreateNew, onHostGame, onEditQuiz, onOpenSession, onCopyQuiz }) {
  const { user, isAdmin, logout } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [finishedSessions, setFinishedSessions] = useState([]);
  const [stats, setStats] = useState({ totalPlays: 0, activeQuizzes: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quizList, quizStats, sessions] = await Promise.all([
        getAllQuizzes(),
        getQuizStats(),
        getFinishedSessions(10),
      ]);
      setQuizzes(quizList);
      setStats(quizStats);
      setFinishedSessions(sessions);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quiz) => {
    const confirmed = window.confirm(`Er du sikker på at du vil slette "${quiz.title}"? Dette kan ikkje angrast.`);
    if (!confirmed) return;
    try {
      await deleteQuiz(quiz.id);
      setQuizzes(quizzes.filter((q) => q.id !== quiz.id));
      setStats((prev) => ({ ...prev, activeQuizzes: prev.activeQuizzes - 1 }));
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Kunne ikkje slette quiz. Prøv igjen.');
    }
  };

  const filteredQuizzes = quizzes.filter((quiz) =>
    quiz.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedQuizzes = searchQuery || showAll
    ? filteredQuizzes
    : filteredQuizzes.slice(0, PREVIEW_COUNT);

  const hasMore = !searchQuery && filteredQuizzes.length > PREVIEW_COUNT;

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-mp-bg-lightgreen font-body text-mp-darkgreen pb-28 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center bg-mp-bg-lightgreen/95 backdrop-blur-sm p-4 justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="flex w-12 h-12 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-mp-1 hover:shadow-mp-2 transition-shadow"
            title="Tilbake til startsida"
          >
            <img src="/mikkel-tenker.png" alt="Mikkel" className="rounded-full w-10 h-10 object-cover" />
          </Link>
          <div>
            <p className="font-body text-sm text-mp-stone-700 leading-none">{getGreeting()},</p>
            <h2 className="font-display text-mp-darkgreen text-xl leading-tight">
              {user?.displayName || user?.email?.split('@')[0] || 'Mikkel'}!
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full hover:bg-white transition-colors text-mp-darkgreen font-display text-sm"
              title="Admin-innstillingar"
            >
              <UserCog size={18} />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          <button
            onClick={logout}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white text-mp-darkgreen transition-colors"
            title="Logg ut"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Search Bar */}
      <div className="px-4 py-2">
        <div className="flex w-full items-center rounded-full bg-white shadow-mp-1 border border-[rgba(17,73,66,0.12)] focus-within:border-mp-teal focus-within:ring-3 focus-within:ring-[rgba(0,137,134,0.18)] transition-all">
          <div className="text-mp-teal pl-4">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="flex w-full min-w-0 flex-1 bg-transparent py-3.5 px-3 font-body text-mp-darkgreen placeholder:text-mp-stone-500 focus:outline-none border-none text-base"
            placeholder="Finn ein kviss …"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Stats Section */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="flex flex-col gap-1 rounded-[20px] p-4 sm:p-5 bg-mp-darkgreen text-white shadow-mp-2 relative overflow-hidden">
            <span className="font-display text-[11px] uppercase tracking-[0.18em] text-mp-lime">Totalt spelt</span>
            <p className="font-display text-3xl text-white tracking-tight mt-1">
              {stats.totalPlays >= 1000 ? `${(stats.totalPlays / 1000).toFixed(1)}k` : stats.totalPlays}
            </p>
          </div>
          <div className="flex flex-col gap-1 rounded-[20px] p-4 sm:p-5 bg-white border border-[rgba(17,73,66,0.12)] shadow-mp-1">
            <span className="font-display text-[11px] uppercase tracking-[0.18em] text-mp-teal">Aktive kvissar</span>
            <p className="font-display text-mp-darkgreen text-3xl tracking-tight mt-1">{stats.activeQuizzes}</p>
          </div>
        </div>
      </div>

      {/* Section Header */}
      <div className="flex items-center justify-between px-4 pt-2 pb-2">
        <h3 className="font-display text-mp-darkgreen text-xl tracking-tight">Mine kvissar</h3>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="font-display text-mp-magenta text-sm hover:underline"
          >
            {showAll ? 'Vis færre' : 'Vis alle'}
          </button>
        )}
      </div>

      {/* Quiz Feed */}
      <div className="flex flex-col gap-4 px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="font-body text-mp-stone-700">Lastar kvissar …</p>
          </div>
        ) : filteredQuizzes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <HelpCircle size={56} className="text-mp-stone-300 mb-4" />
            <h4 className="font-display text-lg text-mp-darkgreen mb-2">
              {searchQuery ? 'Fann ingen kvissar' : 'Ingen kvissar enno'}
            </h4>
            <p className="font-body text-mp-stone-700 mb-4">
              {searchQuery ? 'Prøv eit anna søkeord' : 'Lag din fyrste kviss for å kome i gang!'}
            </p>
            {!searchQuery && (
              <button onClick={onCreateNew} className="mp-cta">Lag kviss</button>
            )}
          </div>
        ) : (
          displayedQuizzes.map((quiz) => (
            <QuizCard
              key={quiz.id}
              quiz={quiz}
              onHostGame={onHostGame}
              onEdit={onEditQuiz}
              onDelete={handleDeleteQuiz}
              onCopy={onCopyQuiz}
            />
          ))
        )}
      </div>

      {/* Siste gjennomforingar */}
      {finishedSessions.length > 0 && (
        <>
          <div className="flex items-center justify-between px-4 pt-6 pb-2">
            <h3 className="font-display text-mp-darkgreen text-xl tracking-tight">Siste gjennomføringar</h3>
          </div>
          <div className="flex flex-col gap-3 px-4 pb-4">
            {finishedSessions.map((session) => {
              const quiz = quizzes.find((q) => q.id === session.quizId);
              const participants = session.participants ? Object.values(session.participants) : [];
              const winner = [...participants].sort((a, b) => (b.score || 0) - (a.score || 0))[0];

              return (
                <div
                  key={session.id}
                  onClick={() => onOpenSession?.(session.id)}
                  className="bg-white rounded-2xl border-l-[6px] border-mp-teal border-y border-r border-[rgba(17,73,66,0.10)] shadow-mp-1 p-4 hover:shadow-mp-2 transition-shadow cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h4 className="font-display text-mp-darkgreen truncate flex-1">{quiz?.title || 'Ukjend kviss'}</h4>
                    <span className="inline-flex font-display text-[10px] px-2.5 py-1 rounded-full bg-mp-bg-lightgreen text-mp-teal uppercase tracking-[0.12em]">Avslutta</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-body text-mp-stone-700">
                    <div className="flex items-center gap-1.5">
                      <Users size={14} className="text-mp-teal" />
                      <span>{participants.length} deltakarar</span>
                    </div>
                    {winner && (
                      <div className="flex items-center gap-1.5">
                        <Trophy size={14} className="text-mp-fox-rust" />
                        <span>
                          {winner.name} ({winner.score || 0} pts)
                        </span>
                      </div>
                    )}
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-mp-stone-500">
                      <Calendar size={12} />
                      {new Date(session.finishedAt || session.createdAt).toLocaleDateString('nn-NO', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={onCreateNew}
          className="group flex items-center justify-center w-14 h-14 hover:w-auto hover:pl-5 hover:pr-6 rounded-full bg-mp-magenta text-white shadow-mp-cta hover:bg-[#8b0053] transition-all duration-300 ease-out overflow-hidden"
          aria-label="Lag ny kviss"
        >
          <Plus size={24} className="shrink-0 group-hover:rotate-90 transition-transform duration-300" />
          <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-48 group-hover:opacity-100 group-hover:ml-2 transition-all duration-300 ease-out font-display text-sm whitespace-nowrap">
            Lag ny kviss
          </span>
        </button>
      </div>
    </div>
  );
}
